import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Building2, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';

interface SiteDisconnectionFormProps {
  workspaceId: string;
  projectId?: string;
  onSuccess?: () => void;
}

export function SiteDisconnectionForm({ workspaceId, projectId, onSuccess }: SiteDisconnectionFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    site_id: '',
    disconnection_date: '',
    reason: '',
    handle_accounts: 'disconnect' as 'disconnect' | 'archive' | 'transfer',
    transfer_to_site_id: '',
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
  });

  const { data: sites } = useQuery({
    queryKey: ['sites', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sites')
        .select('id, name, customers!inner(name)')
        .eq('workspace_id', workspaceId)
        .eq('status', 'active')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspaceId,
  });

  const createRequestMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const requestData = {
        site_id: formData.site_id,
        disconnection_date: formData.disconnection_date,
        reason: formData.reason,
        handle_accounts: formData.handle_accounts,
        transfer_to_site_id: formData.handle_accounts === 'transfer' ? formData.transfer_to_site_id : null,
      };

      const { data, error } = await supabase
        .from('tum_change_management_requests')
        .insert({
          workspace_id: workspaceId,
          onboarding_project_id: projectId || null,
          request_type: 'site_disconnection',
          request_data: requestData,
          description: formData.description,
          priority: formData.priority,
          requested_effective_date: formData.disconnection_date || null,
          requested_by: user.id,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['change-management-counts', workspaceId] });
      toast({
        title: 'Site disconnection request submitted',
        description: 'Your request has been submitted and is pending approval.',
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error submitting request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.site_id) {
      toast({
        title: 'Site required',
        description: 'Please select a site',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.disconnection_date) {
      toast({
        title: 'Disconnection date required',
        description: 'Please select a disconnection date',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.reason.trim()) {
      toast({
        title: 'Reason required',
        description: 'Please provide a reason for disconnection',
        variant: 'destructive',
      });
      return;
    }

    if (formData.handle_accounts === 'transfer' && !formData.transfer_to_site_id) {
      toast({
        title: 'Transfer site required',
        description: 'Please select a site to transfer accounts to',
        variant: 'destructive',
      });
      return;
    }

    createRequestMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Site Disconnection Request
        </CardTitle>
        <CardDescription>
          Disconnect a site from the system. Choose how to handle associated accounts.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="site">Site to Disconnect *</Label>
            <Select
              value={formData.site_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, site_id: value }))}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select site" />
              </SelectTrigger>
              <SelectContent>
                {sites?.map((site: any) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.name} - {site.customers?.name || 'Unknown Customer'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="disconnectionDate">Disconnection Date *</Label>
            <Input
              id="disconnectionDate"
              type="date"
              value={formData.disconnection_date}
              onChange={(e) => setFormData(prev => ({ ...prev, disconnection_date: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason *</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Explain why this site should be disconnected..."
              required
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="handleAccounts">Handle Associated Accounts *</Label>
            <Select
              value={formData.handle_accounts}
              onValueChange={(value: any) => setFormData(prev => ({ ...prev, handle_accounts: value, transfer_to_site_id: '' }))}
              required
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="disconnect">Disconnect (Remove from active accounts)</SelectItem>
                <SelectItem value="archive">Archive (Keep but mark as inactive)</SelectItem>
                <SelectItem value="transfer">Transfer to Another Site</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Choose how to handle accounts associated with this site
            </p>
          </div>

          {formData.handle_accounts === 'transfer' && (
            <div className="space-y-2">
              <Label htmlFor="transferSite">Transfer To Site *</Label>
              <Select
                value={formData.transfer_to_site_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, transfer_to_site_id: value }))}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select target site" />
                </SelectTrigger>
                <SelectContent>
                  {sites?.filter((s: any) => s.id !== formData.site_id).map((site: any) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name} - {site.customers?.name || 'Unknown Customer'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Additional Notes</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Any additional information..."
              rows={3}
            />
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This will disconnect the site and handle associated accounts according to your selection. This action requires approval.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button 
              type="submit"
              disabled={createRequestMutation.isPending}
              className="flex-1"
            >
              {createRequestMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

